import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import './Feedback.css';

// Replace with your key from https://web3forms.com (or set REACT_APP_WEB3FORMS_KEY in .env).
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || '6d3ef851-9311-445a-9f3a-248d8723acea';
const ENDPOINT = 'https://api.web3forms.com/submit';
const MAX_MESSAGE_LENGTH = 2000;

const Feedback = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useBodyScrollLock(open);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const resetForm = useCallback(() => {
    setEmail('');
    setMessage('');
    setStatus('idle');
    setErrorMsg('');
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, closeModal]);

  // Reset form a beat after closing so the success state doesn't flash on reopen
  useEffect(() => {
    if (open) return;
    const t = setTimeout(resetForm, 300);
    return () => clearTimeout(t);
  }, [open, resetForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    setErrorMsg('');

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject: 'Portfolio feedback',
          from_name: email || 'Anonymous visitor',
          email: email || undefined,
          message,
          page: window.location.href,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="feedback-button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
      >
        <svg className="feedback-button-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span className="feedback-button-label">Feedback</span>
      </button>

      {open && createPortal(
        <div className="feedback-overlay" onClick={closeModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Send feedback">
            <button
              type="button"
              className="feedback-close"
              onClick={closeModal}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            {status === 'success' ? (
              <div className="feedback-success">
                <h2>Thanks for the feedback</h2>
                <p>Got it — appreciate you taking the time.</p>
                <div className="feedback-success-actions">
                  <button type="button" className="feedback-submit" onClick={resetForm}>
                    Send another
                  </button>
                  <button type="button" className="feedback-secondary" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="feedback-form">
                <h2>Send feedback</h2>

                <div className="feedback-field">
                  <label className="feedback-label" htmlFor="feedback-email">
                    Email <span className="feedback-optional">(optional)</span>
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>

                <div className="feedback-field">
                  <label className="feedback-label" htmlFor="feedback-message">
                    Message
                  </label>
                  <div className="feedback-textarea-wrap">
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What's on your mind?"
                      rows={5}
                      maxLength={MAX_MESSAGE_LENGTH}
                      aria-describedby="feedback-count"
                      required
                    />
                    <span id="feedback-count" className="feedback-count" aria-live="polite">
                      {message.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                    <svg className="feedback-resize-grip" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                      <path d="M9 1L1 9M9 5L5 9" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                {status === 'error' && (
                  <p className="feedback-error" role="alert">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  className="feedback-submit"
                  disabled={submitting || !message.trim()}
                >
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Feedback;
